import { Dialog } from '@angular/cdk/dialog'
import { NestedTreeControl } from '@angular/cdk/tree'
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree'
import { ActivatedRoute } from '@angular/router'
import { firstValueFrom } from 'rxjs'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { ProductService } from '../../../../services/product.service'
import { RouteService } from '../../../../services/route.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { ApiError } from '../../../../types/apiError'
import { ProductComponent } from '../../../../types/chemistry'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ROUTES } from '../../../../constants/routes'
import { TranslationService } from '../../../../services/translation.service'
import { CommonModule } from '@angular/common'

@Component({
  selector: 'app-chemistry-nav',
  imports: [
    CommonModule,
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './chemistry-nav.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './chemistry-nav.component.scss',
})
export class ChemistryNavComponent implements OnInit {
  route = inject(ActivatedRoute)
  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  routeService = inject(RouteService)
  productService = inject(ProductService)
  spinnerService = inject(SpinnerService)
  errorService = inject(ErrorService)
  translationService = inject(TranslationService)

  treeControl = new NestedTreeControl<ProductComponent>(
    (node) => node.components,
  )
  dataSource = new MatTreeNestedDataSource<ProductComponent>()

  chemistry?: ProductComponent
  selected?: ProductComponent
  @Output() selectedProductChange: EventEmitter<ProductComponent> =
    new EventEmitter<ProductComponent>()

  async ngOnInit() {
    try {
      this.spinnerService.startSpinner()
      const productId = this.route.parent?.snapshot.params['id']
      this.chemistry = this.selected = await firstValueFrom(
        this.productService.getComponents(productId),
      )

      if (!this.chemistry) {
        throw Error('Product chemistry must be provided')
      }

      const currentRoute = this.routeService.getProductViewRoutes(
        this.chemistry.articleName,
        ROUTES.PRODUCT_COMPONENTS.path,
        productId,
      )
      this.productService.currentProductData.set({
        id: this.chemistry.id,
        name: this.chemistry.articleName,
        brand: this.chemistry.brand,
      })

      this.routeService.setRoute(currentRoute)

      this.dataSource.data = [this.chemistry]

      this.setProductChemistry(this.chemistry)

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  hasChild = (_: number, node: ProductComponent) =>
    !!node.components && node.components.length > 0

  /**
   * Emits Product Chemistry info to be displayed
   * @param component
   */
  setProductChemistry(component: ProductComponent) {
    this.selected = component
    this.selectedProductChange.emit(component)
  }

  /**
   * Determines if a node is the root
   * @param {ProductComponent} node The node
   * @returns {boolean} Flag indicating if the node is the root
   */
  isRoot(node: ProductComponent) {
    return node.id == this.chemistry?.id
  }
}
